import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { GlobalContext } from "../GlobalContext";
import { useAuth } from "../AuthContext";
import { useScrollRestore } from "../../Layout/Common/useScrollRestore";
import {
  clearPendingRestoreTargets,
  fetchTillCheckoutState,
  setCardReentryNotice,
  setPendingRestoreTargets,
  setRestoreOutcome,
  setSuppressAutoScroll,
  settleTillCheckoutAsCancel,
} from "./tillRedirect";
import { readTillReturn } from "./useTillReturnSettlement";

const RESTORE_SAFETY_MS = 10000;

export const useTillCheckoutRestore = ({ settling, checkoutState } = {}) => {
  const [tillReturn] = useState(() => readTillReturn());
  const globalcontext = useContext(GlobalContext);
  const { userId } = useAuth();
  const {
    CartItem,
    setPoNumber,
    setOrderCommands,
    setDeliveryMethod,
    setPromoCode,
    setSmsNumber,
    setSelectedPaymentType,
    setNewcardSave,
    setNewcardName,
    setNewcardExp,
    setAddressType,
    setSelectedAddressId,
    setAddressOrderId,
    setNewAddress,
    setShippingAddress,
    setChangeAddressClicked,
    setRefreshable,
    smsNotificationCancelClicked,
  } = globalcontext;

  const returnedWithFailure = tillReturn != null && !tillReturn.isSuccess;

  const [restoring, setRestoring] = useState(returnedWithFailure);
  const [restoredUi, setRestoredUi] = useState(null);
  const [scrollReady, setScrollReady] = useState(false);
  const [scrollDone, setScrollDone] = useState(false);
  const appliedRef = useRef(false);
  const probedRef = useRef(false);
  const abandonedRef = useRef(false);

  const adoptState = (ui) => {
    if (ui) {
      setPendingRestoreTargets({
        shipServiceKey: ui.shipServiceKey,
        cardUuid: ui.selectedCardUuid,
        paymentType: ui.selectedPaymentType,
        deliveryMethod: ui.deliveryMethod,
        shippingAddress: ui.addressType != null ? ui.shippingAddress : null,
        orderCommentsOpen: ui.orderCommentsOpen,
      });
      setSuppressAutoScroll(ui.scrollY > 0);
    }
    setRestoredUi(ui || {});
  };

  useEffect(() => {
    if (!returnedWithFailure || probedRef.current) return;
    if (settling) return;
    probedRef.current = true;
    adoptState(checkoutState);
  }, [returnedWithFailure, settling, checkoutState]);

  useEffect(() => {
    if (tillReturn != null || probedRef.current) return undefined;
    if (!CartItem || CartItem.orderid == null) return undefined;
    probedRef.current = true;

    let cancelled = false;
    fetchTillCheckoutState(CartItem.orderid).then((ui) => {
      if (cancelled || !ui) return;
      abandonedRef.current = true;
      settleTillCheckoutAsCancel({ orderId: CartItem.orderid, userId });
      adoptState(ui);
    });

    return () => {
      cancelled = true;
    };
  }, [tillReturn, CartItem]);

  const finish = () => {
    setRestoring(false);
    setScrollReady(true);
  };

  useLayoutEffect(() => {
    if (restoredUi == null || appliedRef.current) return;
    appliedRef.current = true;

    const ui = restoredUi;
    setRefreshable(false);

    if (ui.poNumber != null) setPoNumber(ui.poNumber);
    if (ui.orderCommands != null) setOrderCommands(ui.orderCommands);
    if (ui.deliveryMethod != null) setDeliveryMethod(ui.deliveryMethod);
    if (ui.promoCode != null) setPromoCode(ui.promoCode);
    if (ui.smsNumber != null) setSmsNumber(ui.smsNumber);
    if (ui.selectedPaymentType != null) setSelectedPaymentType(ui.selectedPaymentType);
    if (ui.newCardSave != null) setNewcardSave(ui.newCardSave);
    if (ui.newcardName != null) setNewcardName(ui.newcardName);
    if (ui.newcardExp != null) setNewcardExp(ui.newcardExp);
    if (ui.addressType != null) setAddressType(ui.addressType);
    if (ui.selectedAddressId != null) setSelectedAddressId(ui.selectedAddressId);
    if (ui.addressOrderId != null) setAddressOrderId(ui.addressOrderId);
    if (ui.newAddress != null) setNewAddress(ui.newAddress);
    if (ui.addressType != null && ui.shippingAddress != null) setShippingAddress(ui.shippingAddress);
    if (ui.changeAddressClicked != null) setChangeAddressClicked(ui.changeAddressClicked);
    if (ui.smsNotificationCancelClicked != null && smsNotificationCancelClicked) {
      smsNotificationCancelClicked.current = ui.smsNotificationCancelClicked;
    }

    if (returnedWithFailure || abandonedRef.current) {
      setRestoreOutcome({
        status: returnedWithFailure ? tillReturn.tillStatus : "cancel",
        transId: returnedWithFailure ? tillReturn.transId : null,
      });
      setCardReentryNotice(ui.selectedPaymentType === "NEWCARD");
    }

    finish();
  }, [restoredUi]);

  useEffect(() => {
    if (!scrollReady) return undefined;
    const timer = setTimeout(() => {
      setSuppressAutoScroll(false);
      setRefreshable(true);
    }, RESTORE_SAFETY_MS);
    return () => clearTimeout(timer);
  }, [scrollReady]);

  useEffect(
    () => () => {
      clearPendingRestoreTargets();
      setSuppressAutoScroll(false);
    },
    [],
  );

  useScrollRestore({
    targetY: restoredUi ? restoredUi.scrollY : null,
    enabled: scrollReady,
    onDone: () => setScrollDone(true),
  });

  return { restoring, revealing: returnedWithFailure && !scrollDone };
};
